// --- Core ---
import {FormattedMessage} from "react-intl";
import {generatePath} from "react-router";
// --- SaniQ ---
import {fetchAPI, generateUrl} from "api";
import {displayErrorbar, displaySnackbar} from "appActions";
import {deserializeVideoConsultation, VideoConsultation} from "../../../models/VideoConsultation";
import {handleResponse} from "helpers/Requests";
import * as VideoConsultationConstants from "../constants/VideoConsultations";

const remindParticipants = async (videoConsultationId: string) => {
  const url = generatePath("/web/v1/physicians/video_consultations/:videoConsultationId/reminders", {videoConsultationId});

  try {
    await fetchAPI(url, 'POST', null, {skipDecoding: true});
    displaySnackbar(
      <FormattedMessage
        id="video_consultation.dialog.remind.success"
        defaultMessage="Participants were reminded by email about the appointment."
      />
    );
  } catch {
    displayErrorbar(
      <FormattedMessage
        id="video_consultation.dialog.remind.error"
        defaultMessage="Participants could not be notified. Please try again later."
      />
    );
  }
};

function fetchVideoConsultationsCount() {
  return dispatch => {
    dispatch(fetchVideoConsultationsCountStarted());
    return fetchAPI(generatePath('/web/v1/physicians/video_consultations/counts'))
      .then((response) => {
        dispatch(fetchVideoConsultationsCountCompleted(response.content));
      });
  };
}

function fetchVideoConsultationsCountStarted() {
  return {
    type: VideoConsultationConstants.FETCH_VIDEO_CONSULTATIONS_COUNT_STARTED
  };
}

function fetchVideoConsultationsCountCompleted(counts) {
  return {
    type: VideoConsultationConstants.FETCH_VIDEO_CONSULTATIONS_COUNT_COMPLETED,
    counts: counts.data
  };
}

function indexVideoConsultations(queries) {
  const url = generateUrl("/web/v1/physicians/video_consultations", undefined, queries);

  return fetchAPI(url, 'GET')
    .then(handleResponse(deserializeVideoConsultation, true));
}

const completeVideoConsultation = (videoConsultationId: string) => {
  const url = generatePath("/web/v1/physicians/video_consultations/:videoConsultationId/statuses/complete", {videoConsultationId});

  return fetchAPI(url, 'PATCH', null).then(handleResponse(deserializeVideoConsultation));
};

const beginCallVideoConsultation = (videoConsultationId: string) => {
  const url = generatePath("/web/v1/physicians/video_consultations/:videoConsultationId/statuses/begin_call", {videoConsultationId});

  return fetchAPI(url, 'PATCH', null).then(handleResponse(deserializeVideoConsultation));
};

const cancelCallVideoConsultation = (videoConsultationId: string) => {
  const url = generatePath("/web/v1/physicians/video_consultations/:videoConsultationId/statuses/cancel", {videoConsultationId});

  return fetchAPI(url, 'PATCH', null).then(handleResponse(deserializeVideoConsultation));
};


function updateVideoConsultationFeedback(videoConsultationId, channelToken, participantId, clientId, feedback, isLoggedIn = false) {
  const urlPrefix = isLoggedIn ? '/web/v1/physicians' : '/public/v1';
  const consultationId = isLoggedIn ? videoConsultationId : channelToken;
  const id = isLoggedIn ? participantId : clientId;
  const url = generatePath(`${urlPrefix}/video_consultations/:consultationId/participants/:id/feedbacks`, {consultationId, id});

  return fetchAPI(url, 'PATCH', {feedback}, {withAuthentication: isLoggedIn})
    .then((response) => response.content);
}

const showVideoConsultationByRoomId = (roomId, tenantName) => {
  const url = generatePath("/public/v1/video_consultations/:roomId", {roomId});

  return fetchAPI(url, 'GET', undefined, {
    ignoreForbidden: true,
    ignoreNotFound: true,
    withAuthentication: false,
    headers: {
      "Tenant-Name": tenantName
    }
  }).then(handleResponse(deserializeVideoConsultation));
};

const createVideoConsultation = (payload: VideoConsultation) => {
  const url = generatePath("/web/v1/physicians/video_consultations");

  return fetchAPI(url, 'POST', {video_consultation: payload});
};

const updateVideoConsultation = (videoConsultationId: string, payload: VideoConsultation) => {
  const url = generatePath("/web/v1/physicians/video_consultations/:videoConsultationId", {videoConsultationId});

  return fetchAPI(url, 'PATCH', {video_consultation: payload});
};

const showVideoConsultation = (videoConsultationId: string) => {
  const url = generatePath("/web/v1/physicians/video_consultations/:videoConsultationId", {videoConsultationId});

  return fetchAPI(url).then(handleResponse(deserializeVideoConsultation));
};

export {
  createVideoConsultation,
  updateVideoConsultation,
  indexVideoConsultations,
  showVideoConsultation,
  showVideoConsultationByRoomId,
  beginCallVideoConsultation,
  completeVideoConsultation,
  cancelCallVideoConsultation,
  updateVideoConsultationFeedback,
  remindParticipants,
  fetchVideoConsultationsCount
};
